import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";

import { useProject } from "../app/projectContext";
import { useStudyDraft } from "../hooks/useStudyDraft";
import type { SldElements } from "../services/sldElements";
import {
  surgeProtectionRunCreateRequestSchema,
  type SurgeProtectionRunCreateRequest,
} from "../services/surgeProtection";
import {
  backupDeviceKindSchema,
  connectionModeSchema,
  coordinationDeclarationSchema,
  earthingSystemSchema,
  locationKindSchema,
  lpsPresenceSchema,
  spdTypeSchema,
  upBasisSchema,
} from "../services/surgeProtectionContract";
import { describeValidationIssue, type ValidationIssueLike } from "../utils/validationMessages";
import { FaultRunSelector } from "./FaultRunSelector";
import { OtherRevisionDraftNotice } from "./OtherRevisionDraftNotice";
import { SldElementPicker } from "./SldElementPicker";
import {
  BACKUP_DEVICE_KIND_LABELS,
  buildSurgeProtectionRunPayload,
  CONNECTION_MODE_LABELS,
  COORDINATION_LABELS,
  createInitialSurgeProtectionDraft,
  createProtectedItemDraft,
  CURRENT_SOURCE_LABELS,
  CURRENT_SOURCES,
  EARTHING_SYSTEM_LABELS,
  LEAD_ROUTE_LABELS,
  LEAD_ROUTES,
  LOCATION_LABELS,
  LPS_LABELS,
  seedProtectedItemIdCounter,
  SPD_TYPE_LABELS,
  SURGE_PROTECTION_DRAFT_MODULE,
  SURGE_PROTECTION_LABELS,
  surgeProtectionDraftSchema,
  UP_BASIS_LABELS,
  type CurrentSourceChoice,
  type LeadRouteChoice,
  type ProtectedItemDraft,
  type SurgeProtectionDraft,
} from "./surgeProtectionDraft";

/** The first option of every choice: nothing is chosen for the engineer. */
const CHOOSE = "Choose…";

export { NO_FAULT_RUNS_NOTE, NO_OPEN_REVISION_NOTE, faultRunOptionText } from "./FaultRunSelector";

export const NOT_MAXIMUM_THREE_PHASE_NOTE =
  "This fault run is not the maximum three-phase fault; the link is recorded as review required " +
  "beside the stored run, the SPD check itself is unchanged.";

/** "The fault run bus <x> differs from the SPD's bus <y>; ..." */
export function busMismatchNote(faultBus: string, studyBus: string): string {
  return (
    `The fault run bus ${faultBus} differs from the SPD's bus ${studyBus}; the link is recorded ` +
    "as review required beside the stored run."
  );
}

export const UW_HINT =
  "Each protected item's rated impulse withstand voltage Uw comes from its data sheet or the " +
  "project design basis, declared with its source; the product applies no overvoltage-category " +
  "table. The effective protection level Up + the lead voltage drop is compared with every Uw; " +
  "equality passes. An item's distance from the SPD is compared with the manufacturer's declared " +
  "protection distance when both are declared.";

export const REQUIREMENTS_HINT =
  "Every requirement - the minimum Uc for the earthing system and the connection mode, the " +
  "required UT, Iimp, In and Imax - is the engineer's declared figure with its source (a project " +
  "design basis, a specification); the product carries no IEC or CPWD value. A requirement left " +
  "blank leaves its check not established, named on the sheet.";

export const SHORT_CIRCUIT_HINT =
  "The SPD assembly's short-circuit current rating is valid only with the back-up device the " +
  "manufacturer declares it with; it is compared with the prospective current Ik'' of the linked " +
  "maximum three-phase fault run at the SPD's bus, or with a declared current with its source. A " +
  "rating below the current is a failure.";

export const PROFILE_NOTE =
  "No profile holds an SPD table in this version: every requirement is the engineer's or the " +
  "manufacturer's declared figure with its source (GAP-029); the arithmetic is universal. The " +
  "profile is recorded with the run and must match the project revision.";

type SurgeProtectionFormProps = {
  disabled?: boolean;
  onSubmit: (payload: SurgeProtectionRunCreateRequest) => void | Promise<void>;
  // The revision's single-line diagram, when it has one (the page reads it): the SPD's bus is
  // picked from it.
  sldElements?: SldElements | null | undefined;
};

// The surge protection study of one SPD location (EOS-10). The form owns the draft; the
// prospective current starts on a linked fault run of the active open revision, its Ik'' read by
// the backend when the study is saved. Every choice starts unchosen: the backend imposes no default.
export function SurgeProtectionForm({ disabled = false, onSubmit, sldElements }: SurgeProtectionFormProps) {
  const { activeRevisionId } = useProject();
  const revisionId = activeRevisionId();
  const { draft, setDraft, restored, clear, otherRevision, keepForThisRevision } =
    useStudyDraft<SurgeProtectionDraft>({
      module: SURGE_PROTECTION_DRAFT_MODULE,
      createInitial: createInitialSurgeProtectionDraft,
      schema: surgeProtectionDraftSchema,
      revisionId,
    });

  const [validationError, setValidationError] = useState<string | null>(null);
  const [itemToFocus, setItemToFocus] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (itemToFocus === null) {
      return;
    }
    formRef.current?.querySelector<HTMLInputElement>(`input[name="${itemToFocus}-code"]`)?.focus();
    setItemToFocus(null);
  }, [itemToFocus]);

  function updateStudy(patch: Partial<SurgeProtectionDraft>) {
    setDraft((current) => ({ ...current, ...patch }));
  }

  function updateItem(id: string, patch: Partial<ProtectedItemDraft>) {
    setDraft((current) => ({
      ...current,
      items: current.items.map((item) => (item.id === id ? { ...item, ...patch } : item)),
    }));
  }

  // Stable for the fault-run selector's effect that clears an unlisted run.
  const setFaultRunId = useCallback(
    (faultRunId: string) => setDraft((current) => ({ ...current, faultRunId })),
    [setDraft],
  );

  function addItem() {
    seedProtectedItemIdCounter(draft.items.map((item) => item.id));
    const item = createProtectedItemDraft();
    setDraft((current) => ({ ...current, items: [...current.items, item] }));
    setItemToFocus(item.id);
  }

  function removeItem(id: string) {
    setDraft((current) => ({ ...current, items: current.items.filter((item) => item.id !== id) }));
  }

  function clearForm() {
    setValidationError(null);
    clear();
  }

  // The selector's own sentence stands alone; every other issue is read by its label.
  function describeIssue(issue: ValidationIssueLike): string {
    if (issue.code === "custom" && issue.path.at(-1) === "fault_run_id") {
      return issue.message;
    }
    return describeValidationIssue(issue, SURGE_PROTECTION_LABELS);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setValidationError(null);

    const parsed = surgeProtectionRunCreateRequestSchema.safeParse(
      buildSurgeProtectionRunPayload(draft),
    );

    if (!parsed.success) {
      const issue = parsed.error.issues[0];
      setValidationError(issue ? describeIssue(issue) : "Review the surge protection inputs.");
      return;
    }

    await onSubmit(parsed.data);
  }

  const studyText = (
    label: string,
    field: keyof SurgeProtectionDraft,
    name: string,
    inputMode: "decimal" | "text" = "text",
  ) => (
    <label>
      {label}
      <input
        inputMode={inputMode}
        name={name}
        value={draft[field] as string}
        onChange={(event) => updateStudy({ [field]: event.target.value } as Partial<SurgeProtectionDraft>)}
      />
    </label>
  );

  const studyChoice = <T extends string>(
    label: string,
    field: keyof SurgeProtectionDraft,
    name: string,
    options: readonly T[],
    labels: Record<T, string>,
  ) => (
    <label>
      {label}
      <select
        name={name}
        value={draft[field] as string}
        onChange={(event) => updateStudy({ [field]: event.target.value } as Partial<SurgeProtectionDraft>)}
      >
        <option value="">{CHOOSE}</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {labels[option]}
          </option>
        ))}
      </select>
    </label>
  );

  // Field names carry the item's id ("spd-item-3-code"), stable across add and remove.
  const itemText = (
    item: ProtectedItemDraft,
    label: string,
    field: keyof ProtectedItemDraft,
    inputMode: "decimal" | "text" = "decimal",
  ) => (
    <label>
      {label}
      <input
        inputMode={inputMode}
        name={`${item.id}-${field}`}
        value={item[field]}
        onChange={(event) => updateItem(item.id, { [field]: event.target.value })}
      />
    </label>
  );

  const carriesIimp = draft.spdType === "TYPE_1" || draft.spdType === "TYPE_1_2";
  const carriesIn = draft.spdType === "TYPE_2" || draft.spdType === "TYPE_1_2";
  const busCode = draft.sldBusCode.trim();

  return (
    <form aria-label="Surge protection inputs" ref={formRef} onSubmit={handleSubmit}>
      {restored ? <p role="status">Draft restored from this session.</p> : null}
      {otherRevision ? (
        <OtherRevisionDraftNotice onKeep={keepForThisRevision} onClear={clearForm} disabled={disabled} />
      ) : null}

      <fieldset disabled={disabled}>
        <legend>Study definition</legend>
        {studyText("Study code", "code", "studyCode")}
        {studyText("Study name", "name", "studyName")}
        {studyText("SPD code", "spdCode", "spdCode")}
        {studyChoice("Location", "locationKind", "locationKind", locationKindSchema.options, LOCATION_LABELS)}
        {studyText("System voltage (V)", "systemVoltageV", "systemVoltageV", "decimal")}
        {studyText("System voltage source", "systemVoltageSource", "systemVoltageSource")}
        {studyChoice("Earthing system", "earthingSystem", "earthingSystem", earthingSystemSchema.options, EARTHING_SYSTEM_LABELS)}
        {studyChoice("Connection mode", "connectionMode", "connectionMode", connectionModeSchema.options, CONNECTION_MODE_LABELS)}
        {studyText("Connection mode source", "connectionModeSource", "connectionModeSource")}
        <SldElementPicker
          label="Bus code"
          name="sldBusCode"
          value={draft.sldBusCode}
          onChange={(value) => updateStudy({ sldBusCode: value })}
          categories={["bus"]}
          elements={sldElements}
        />
        {studyChoice("Lightning protection system", "lpsPresence", "lpsPresence", lpsPresenceSchema.options, LPS_LABELS)}
        {studyText("Upstream SPD", "upstreamSpdCode", "upstreamSpdCode")}
        {studyText("Downstream SPD", "downstreamSpdCode", "downstreamSpdCode")}
        <label>
          Jurisdiction profile
          <select
            name="jurisdictionProfile"
            value={draft.jurisdictionProfile}
            onChange={(event) => updateStudy({ jurisdictionProfile: event.target.value })}
          >
            <option value="IN">India (CEA Regulations, IS, CPWD)</option>
            <option value="IEC">IEC (international, no national layer)</option>
            <option value="UK">United Kingdom (BS 7671) - reference data pending</option>
            <option value="EU">European Union (HD 60364) - reference data pending</option>
            <option value="US">United States (NEC / NFPA 70) - reference data pending</option>
            <option value="AU_NZ">Australia / New Zealand (AS/NZS 3000) - reference data pending</option>
          </select>
        </label>
        <p data-profile-note="true">{PROFILE_NOTE}</p>
        {studyText("Study notes", "notes", "studyNotes")}
      </fieldset>

      <fieldset disabled={disabled}>
        <legend>Prospective current</legend>
        <label>
          Prospective current from
          <select
            name="currentSource"
            value={draft.currentSource}
            onChange={(event) => updateStudy({ currentSource: event.target.value as CurrentSourceChoice })}
          >
            {CURRENT_SOURCES.map((source) => (
              <option key={source} value={source}>
                {CURRENT_SOURCE_LABELS[source]}
              </option>
            ))}
          </select>
        </label>
        {draft.currentSource === "LINKED" ? (
          <FaultRunSelector
            revisionId={revisionId}
            faultRunId={draft.faultRunId}
            onChange={setFaultRunId}
            busCode={busCode}
            withPeak={false}
            notMaximumNote={NOT_MAXIMUM_THREE_PHASE_NOTE}
            busMismatchNoteFor={busMismatchNote}
          />
        ) : null}
        {draft.currentSource === "DECLARED" ? (
          <>
            {studyText("Prospective current (kA)", "prospectiveCurrentKa", "prospectiveCurrentKa", "decimal")}
            {studyText("Prospective current source", "prospectiveCurrentSource", "prospectiveCurrentSource")}
          </>
        ) : null}
        <p data-short-circuit-hint="true">{SHORT_CIRCUIT_HINT}</p>
      </fieldset>

      <fieldset disabled={disabled}>
        <legend>SPD parameters</legend>
        {studyChoice("SPD type", "spdType", "spdType", spdTypeSchema.options, SPD_TYPE_LABELS)}
        {studyText("Up (kV)", "upKv", "upKv", "decimal")}
        {studyText("Up source", "upSource", "upSource")}
        {studyChoice("Up stated at", "upBasis", "upBasis", upBasisSchema.options, UP_BASIS_LABELS)}
        {studyText("Uc (V)", "ucV", "ucV", "decimal")}
        {studyText("Uc source", "ucSource", "ucSource")}
        {studyText("UT (V)", "utV", "utV", "decimal")}
        {studyText("UT source", "utSource", "utSource")}
        {carriesIimp ? (
          <>
            {studyText("Iimp (kA)", "iimpKa", "iimpKa", "decimal")}
            {studyText("Iimp source", "iimpSource", "iimpSource")}
            <label>
              <input
                type="checkbox"
                name="iimpPerModeDeclared"
                checked={draft.iimpPerModeDeclared}
                onChange={(event) => updateStudy({ iimpPerModeDeclared: event.target.checked })}
              />
              Iimp declared per mode
            </label>
          </>
        ) : null}
        {carriesIn ? (
          <>
            {studyText("In (kA)", "inKa", "inKa", "decimal")}
            {studyText("In source", "inSource", "inSource")}
            {studyText("Imax (kA)", "imaxKa", "imaxKa", "decimal")}
            {studyText("Imax source", "imaxSource", "imaxSource")}
          </>
        ) : null}
        {studyText("Short-circuit current rating (kA)", "isccrKa", "isccrKa", "decimal")}
        {studyText("Short-circuit rating source", "isccrSource", "isccrSource")}
        {studyChoice("Back-up device kind", "backupKind", "backupKind", backupDeviceKindSchema.options, BACKUP_DEVICE_KIND_LABELS)}
        {studyText("Back-up device rating (A)", "backupRatingA", "backupRatingA", "decimal")}
        {studyText("Back-up device source", "backupSource", "backupSource")}
        <SldElementPicker
          label="Back-up device (SLD)"
          name="backupSldDeviceCode"
          value={draft.backupSldDeviceCode}
          onChange={(value) => updateStudy({ backupSldDeviceCode: value })}
          categories={["device"]}
          elements={sldElements}
        />
        {studyText("Protection distance (m)", "protectionDistanceM", "protectionDistanceM", "decimal")}
        {studyText("Protection distance source", "protectionDistanceSource", "protectionDistanceSource")}
        {studyChoice("Energy coordination", "coordination", "coordination", coordinationDeclarationSchema.options, COORDINATION_LABELS)}
        {draft.coordination === "DECLARED_BY_MANUFACTURER" ? (
          <>
            {studyText("Coordination source", "coordinationSource", "coordinationSource")}
            {studyText("Coordinated SPD", "coordinatedSpdReference", "coordinatedSpdReference")}
          </>
        ) : null}
        {studyText("Manufacturer reference", "manufacturerReference", "manufacturerReference")}
      </fieldset>

      <fieldset disabled={disabled}>
        <legend>Connecting leads</legend>
        <label>
          Lead declaration
          <select
            name="leadRoute"
            value={draft.leadRoute}
            onChange={(event) => updateStudy({ leadRoute: event.target.value as LeadRouteChoice })}
          >
            {LEAD_ROUTES.map((route) => (
              <option key={route} value={route}>
                {LEAD_ROUTE_LABELS[route]}
              </option>
            ))}
          </select>
        </label>
        {draft.leadRoute === "LENGTH" ? (
          <>
            {studyText("Lead length (m)", "leadLengthM", "leadLengthM", "decimal")}
            {studyText("Lead length source", "leadLengthSource", "leadLengthSource")}
            {studyText("Inductive drop (kV/m)", "leadDropKvPerM", "leadDropKvPerM", "decimal")}
            {studyText("Inductive drop source", "leadDropSource", "leadDropSource")}
          </>
        ) : null}
        {draft.leadRoute === "VOLTAGE" ? (
          <>
            {studyText("Lead voltage drop (kV)", "leadVoltageKv", "leadVoltageKv", "decimal")}
            {studyText("Lead voltage source", "leadVoltageSource", "leadVoltageSource")}
          </>
        ) : null}
      </fieldset>

      <fieldset disabled={disabled}>
        <legend>Requirements</legend>
        {studyText("Minimum Uc (V)", "ucMinimumV", "ucMinimumV", "decimal")}
        {studyText("Minimum Uc source", "ucMinimumSource", "ucMinimumSource")}
        {studyText("Required UT (V)", "utRequiredV", "utRequiredV", "decimal")}
        {studyText("Required UT source", "utRequiredSource", "utRequiredSource")}
        {studyText("Required Iimp (kA)", "iimpRequiredKa", "iimpRequiredKa", "decimal")}
        {studyText("Required Iimp source", "iimpRequiredSource", "iimpRequiredSource")}
        {studyText("Required In (kA)", "inRequiredKa", "inRequiredKa", "decimal")}
        {studyText("Required In source", "inRequiredSource", "inRequiredSource")}
        {studyText("Required Imax (kA)", "imaxRequiredKa", "imaxRequiredKa", "decimal")}
        {studyText("Required Imax source", "imaxRequiredSource", "imaxRequiredSource")}
        <p data-requirements-hint="true">{REQUIREMENTS_HINT}</p>
      </fieldset>

      <fieldset disabled={disabled}>
        <legend>Protected items</legend>
        {draft.items.map((item, index) => {
          const number = index + 1;
          return (
            <fieldset key={item.id} data-protected-item-draft={item.id}>
              <legend>Item {number}</legend>
              {itemText(item, "Item code", "code", "text")}
              {itemText(item, "Item name", "name", "text")}
              {itemText(item, "Uw (kV)", "uwKv")}
              {itemText(item, "Uw source", "uwSource", "text")}
              {itemText(item, "Distance from the SPD (m)", "distanceM")}
              {itemText(item, "Item notes", "notes", "text")}
              {draft.items.length > 1 ? (
                <button type="button" onClick={() => removeItem(item.id)}>
                  Remove item {number}
                </button>
              ) : null}
            </fieldset>
          );
        })}
        <button type="button" onClick={addItem}>
          Add item
        </button>
        <p data-uw-hint="true">{UW_HINT}</p>
      </fieldset>

      {validationError ? <p role="alert">{validationError}</p> : null}

      <button disabled={disabled} type="submit">
        Check surge protection
      </button>
      <button disabled={disabled} type="button" onClick={clearForm}>
        Clear form
      </button>
    </form>
  );
}

export default SurgeProtectionForm;
